
import React, { useEffect, useRef, useState } from 'react'
import { checkaddress } from 'api/urls'
import { POST } from 'api/request'
import './index.scss'

export const SPECIAL_ADDRESS_KEY = 'addressObject'

type AddressType = 'city' | 'street' | 'estateNumber' | 'zip' | 'district' | 'province' | 'address'
type ValueObject = Record<string, any>

interface AddressInputProps {
  value?: string
  error?: boolean
  placeHolder?: string
  inputName?: AddressType
  valueObject?: ValueObject
  setValue: (address: string, value?: CheckAddress) => void
}

export interface CheckAddress {
  street: string
  number: string
  city: string
  cityDistricted: string
  sublocality: string
  province: string
  district: string
  zip: string
  gpslatitude: number
  gpslongitude: number
  readable: string
  streetPrefix: string
}

const AddressInput: React.FC<AddressInputProps> = ({ valueObject, value, inputName = 'address', setValue, placeHolder, error }) => {
  const [options, setOptions] = useState<CheckAddress[] | null>(null)
  const [open, setOpen] = useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const timeout = useRef<NodeJS.Timeout | null>(null)


  useEffect(() => {
    const closeHandler = (e: MouseEvent) => {
      !wrapperRef.current?.contains(e.target as Node) && setOpen(false)
    }
    document.addEventListener('click', closeHandler)
    return () => document.removeEventListener('click', closeHandler)
  }, [])

  const getNewOptions = async (value: string) => {
    const response = await POST<CheckAddress[]>(checkaddress, {
      body: getRequestBody(inputName, valueObject, value)
    })
    if (response.ok) {
      setOptions(response.data)
      setOpen(true)
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={`address__select__wrapper${error ? ' has-error' : ''}${open && options && options.length > 0 ? ' active' : ''}`}>
      <div className='address__select'>
        <input
          value={value || ''}
          id={`input-${inputName}`}
          placeholder={placeHolder}
          className={error ? 'has-error' : value ? '--activated' : ''}
          onChange={e => {
            const { value } = e.target
            if (timeout.current) clearTimeout(timeout.current)
            timeout.current = setTimeout(() => getNewOptions(value), 200)
            setValue(value)
          }} />
        {options && options.length > 0 &&
          <i
            className={`icon icon--arrow-${open ? 'up' : 'down'}`}
            onClick={e => {
              e.stopPropagation()
              setOpen((prev) => !prev)
            }}
          />
        }
      </div>
      {options && <div className='address__select__dropdown'>
        <ul className='address__select__dropdown__items'>
          {options.map((item, idx) =>
            <li
              key={idx}
              className='address__select__dropdown__item'
              onClick={() => {
                setValue(displayOption('address', item), item)
                setOpen(false)
              }} >
              {displayOption(inputName, item)}
            </li>
          )}
        </ul>
      </div>}
    </div>
  )
}

const getRequestBody = (key: AddressType, valueObject?: ValueObject, value?: string) => {
  switch (key) {
    case 'address': return { find: 'address', address: value }
    case 'city': return { find: 'city', city: value || '' }
    case 'street': return { find: 'street', city: valueObject?.city, street: value || '' }
    case 'estateNumber': return { find: 'houseNumber', city: valueObject?.city, street: valueObject?.street, houseNumber: value || '' }
    case 'zip': return { find: 'zip', city: valueObject?.city, street: valueObject?.zip, zip: value || '' }
    case 'district': return { find: 'district', city: valueObject?.city, street: valueObject?.street, district: value || '' } // ??
    case 'province': return { find: 'province', city: valueObject?.city, province: value || '' } // ??
  }
}

const displayOption = (key: AddressType, item: CheckAddress) => {
  switch (key) {
    case 'address': return `${item.zip || ''} ${item.city || ''}${item.street ? ', ' : ''}${item.streetPrefix || ''}${item.street || ''} ${item.number || ''}`
    case 'city': return `${item.city}, pow. ${item.district}`
    case 'street': return `${item.streetPrefix} ${item.street}`
    case 'estateNumber': return `${item.number}`
    default: return `${item[key]}`
  }
}

export default AddressInput